import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import { useEffect, useLayoutEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  BuildJobStatusSchema,
  BuildTypeSchema,
} from "../../../darvin-shared/schema/zod/build";
import { BuildApi } from "../../api/buildApi";
import {
  useIsAuthInitialized,
  useIsUserSignedIn,
} from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import {
  BuildPlatform,
  useProjectBuildActions,
  useProjectBuildCompletedBuild,
  useProjectBuildErrorBuild,
  useProjectBuildsProcessingBuildIds,
} from "../../core_stores/useProjectBuildsStore";
import {
  ExportStatus,
  useProjectExportsActions,
  useProjectExportStatus,
  useProjectLatestExport,
} from "../../core_stores/useProjectExportsStore";
import { useProjectFilesListActions } from "../../core_stores/useProjectFilesListStore";
import {
  useProjectActions,
  useProjectId,
  useProjectStore,
} from "../../core_stores/useProjectStore";
import { ImagePreviewProvider } from "../../providers/ImagePreviewProvider";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { logProjectEvent } from "../../utils/logUtils";
import { useChatMessagesActions } from "../../view_stores/chat_messages/useChatMessagesStore";
import {
  DashboardMode,
  useDashboardControlsActions,
  useDashboardMode,
  WorkspaceSection,
} from "../../view_stores/useDashboardControlsStore";
import { useProjectConfigActions } from "../../view_stores/useProjectConfigStore";
import { useProjectIconActions } from "../../view_stores/useProjectIconStore";
import {
  PreviewMode,
  useProjectPreviewActions,
} from "../../view_stores/useProjectPreviewStore";
import { useProjectSettingsActions } from "../../view_stores/useProjectSettingsStore";
import { useProjectStatusActions } from "../../view_stores/useProjectStatusStore";
import { usePromptInputActions } from "../../view_stores/usePromptInputStore";
import { useRollbackErrorHandlerActions } from "../../view_stores/useRollbackErrorHandlerStore";
import { useRollbackHandlerActions } from "../../view_stores/useRollbackHandlerStore";
import { useSideMenuActions } from "../../view_stores/useSideMenuStore";
import { useTechnicalErrorHandlerActions } from "../../view_stores/useTechnicalErrorHandlerStore";
import { ProjectPageCompact } from "./ProjectPageCompact";
import { ProjectPageDefault } from "./ProjectPageDefault";

// Helper function to detect mobile/tablet devices
const isMobileOrTabletDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(
    userAgent
  );
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(
    userAgent
  );
  return isMobile || isTablet;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      networkMode: "always",
    },
  },
});

export function ProjectPage() {
  const location = useLocation();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const isAuthInitialized = useIsAuthInitialized();
  const isUserSignedIn = useIsUserSignedIn();
  const dashboardMode = useDashboardMode();
  const initializedProjectId = useProjectId();
  const completedBuild = useProjectBuildCompletedBuild();
  const errorBuild = useProjectBuildErrorBuild();
  const projectExportStatus = useProjectExportStatus();

  const { setSelectedProjectId } = useSideMenuActions();
  const {
    setDashboardMode,
    setWorkspaceSection,
    resetWorkspaceHistory,
    reset: resetDashboardControls,
  } = useDashboardControlsActions();
  const { setPublishPlatform } = useDashboardControlsActions();
  const {
    initialize: initializeProject,
    dispose: disposeProject,
    disposeSSE: disposeProjectSSE,
    refresh: refreshProject,
  } = useProjectActions();
  const {
    initialize: initializeProjectPreview,
    dispose: disposeProjectPreview,
  } = useProjectPreviewActions();
  const { initialize: initializeProjectConfig, dispose: disposeProjectConfig } =
    useProjectConfigActions();
  const { initialize: initializeProjectSettings } = useProjectSettingsActions();
  const {
    startListening: startChatMessagesListening,
    addTemporaryMessage,
    dispose: disposeChatMessages,
  } = useChatMessagesActions();
  const { initialize: initializeProjectStatus, dispose: disposeProjectStatus } =
    useProjectStatusActions();
  const {
    initialize: initializeTechnicalErrorHandler,
    dispose: disposeTechnicalErrorHandler,
  } = useTechnicalErrorHandlerActions();
  const {
    initialize: initializeRollbackErrorHandler,
    dispose: disposeRollbackErrorHandler,
  } = useRollbackErrorHandlerActions();
  const { setPreviewMode } = useProjectPreviewActions();
  const {
    dispose: disposeProjectBuilds,
    resetCompletedBuild,
    resetErrorBuild,
  } = useProjectBuildActions();
  const { dispose: disposeProjectExports } = useProjectExportsActions();
  const isCompactView = useIsCompactView();
  const { clear: clearPromptInput } = usePromptInputActions();
  const { reset: resetProjectFilesList } = useProjectFilesListActions();
  const { showSnackbar, hideAllSnackbars } = useSnackbar();
  const { clear: clearProjectIcon } = useProjectIconActions();
  const { reset: resetRollbackHandler } = useRollbackHandlerActions();

  // Handle inactivity refresh for mobile devices because sometimes the SSE connection is kind of "paused" and the onAbort is not called
  useEffect(() => {
    if (!isMobileOrTabletDevice()) return;

    const INACTIVITY_STORAGE_KEY = "project-mobile-inactivity-start-time";
    const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

    let inactivityStartTime: number | null = null;

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        inactivityStartTime = Date.now();
        localStorage.setItem(
          INACTIVITY_STORAGE_KEY,
          inactivityStartTime.toString()
        );
      } else if (document.visibilityState === "visible") {
        if (!inactivityStartTime) {
          const storedTime = localStorage.getItem(INACTIVITY_STORAGE_KEY);
          if (!storedTime) {
            return;
          }
          const parsedTime = parseInt(storedTime, 10);
          if (isNaN(parsedTime)) {
            return;
          }
          inactivityStartTime = parsedTime;
        }

        const elapsedTime = Date.now() - inactivityStartTime;
        if (elapsedTime >= INACTIVITY_TIMEOUT_MS) {
          refreshProject();
        }
        inactivityStartTime = null;
        localStorage.removeItem(INACTIVITY_STORAGE_KEY);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useLayoutEffect(() => {
    // Wait for auth to be initialized before proceeding
    if (!isAuthInitialized) {
      return;
    }

    const initialPrompt = location.state?.initialPrompt;
    const initialImage = location.state?.initialImage;

    // Clear location state to prevent re-sending prompt on browser back navigation
    if (initialPrompt) {
      window.history.replaceState({}, document.title);
    }

    // Reset stores when switching projects
    if (urlProjectId !== initializedProjectId) {
      disposeChatMessages();
      disposeProjectStatus();
      disposeProject();
      resetDashboardControls();
      disposeProjectPreview();
      disposeProjectConfig();
      disposeProjectBuilds();
      disposeProjectExports();
      disposeTechnicalErrorHandler();
      disposeRollbackErrorHandler();
      resetProjectFilesList();
      clearPromptInput();
      clearProjectIcon();
      resetRollbackHandler();
      hideAllSnackbars();

      if (!isCompactView) {
        setWorkspaceSection(WorkspaceSection.SETTINGS);
        setDashboardMode(DashboardMode.PREVIEW);
        setPreviewMode(PreviewMode.MobilePortrait);
      }
    }

    if (urlProjectId && isUserSignedIn) {
      startChatMessagesListening();
      initializeProjectStatus();
      initializeTechnicalErrorHandler();
      initializeRollbackErrorHandler();
      initializeProjectPreview();
      initializeProjectConfig();
      initializeProjectSettings();
      setSelectedProjectId(urlProjectId);

      const initializeProjectWithInitialPrompt = async () => {
        if (initialPrompt && !initializedProjectId) {
          await addTemporaryMessage(initialPrompt, initialImage);
        }
        initializeProject(urlProjectId, initialPrompt, initialImage);
      };
      initializeProjectWithInitialPrompt();
    } else {
      disposeProjectSSE();
    }
  }, []);

  useEffect(() => {
    if (isCompactView) {
      setDashboardMode(null);
      setWorkspaceSection(null);
      resetWorkspaceHistory();
    } else if (dashboardMode === null) {
      setDashboardMode(DashboardMode.PREVIEW);
      setWorkspaceSection(WorkspaceSection.SETTINGS);
    }
  }, [isCompactView]);

  useEffect(() => {
    if (completedBuild) {
      let prefix = "";
      let publishPlatform = null;

      if (completedBuild === BuildTypeSchema.enum.web) {
        prefix = "Web";
        publishPlatform = BuildPlatform.WEB;
      } else if (completedBuild === BuildTypeSchema.enum.apk) {
        prefix = "Android APK";
        publishPlatform = BuildPlatform.ANDROID;
      } else if (completedBuild === BuildTypeSchema.enum.aab) {
        prefix = "Android AAB";
        publishPlatform = BuildPlatform.ANDROID;
      }

      if (prefix !== "" && publishPlatform) {
        showSnackbar(`${prefix} build completed successfully.`, {
          duration: null,
          onClick: () => {
            setDashboardMode(DashboardMode.WORKSPACE);
            setWorkspaceSection(WorkspaceSection.PUBLISH);
            setPublishPlatform(publishPlatform);
          },
        });
        resetCompletedBuild();
      }
    }
  }, [completedBuild]);

  useEffect(() => {
    if (errorBuild) {
      let prefix = "";
      let publishPlatform = null;

      if (errorBuild === BuildTypeSchema.enum.web) {
        prefix = "Web";
        publishPlatform = BuildPlatform.WEB;
      } else if (errorBuild === BuildTypeSchema.enum.apk) {
        prefix = "Android APK";
        publishPlatform = BuildPlatform.ANDROID;
      } else if (errorBuild === BuildTypeSchema.enum.aab) {
        prefix = "Android AAB";
        publishPlatform = BuildPlatform.ANDROID;
      }

      if (prefix !== "" && publishPlatform) {
        showSnackbar(`${prefix} build failed.`, {
          type: "error",
          duration: null,
          onClick: () => {
            setDashboardMode(DashboardMode.WORKSPACE);
            setWorkspaceSection(WorkspaceSection.PUBLISH);
            setPublishPlatform(publishPlatform);
          },
        });
        resetErrorBuild();
      }
    }
  }, [errorBuild]);

  useEffect(() => {
    if (projectExportStatus === ExportStatus.DONE) {
      showSnackbar("Your app export package is ready to download!", {
        duration: null,
        onClick: () => {
          setDashboardMode(DashboardMode.WORKSPACE);
          setWorkspaceSection(WorkspaceSection.EXPORT);
        },
      });
    } else if (projectExportStatus === ExportStatus.ERROR) {
      showSnackbar("Your app export package build failed due to an error!", {
        type: "error",
        duration: null,
        onClick: () => {
          setDashboardMode(DashboardMode.WORKSPACE);
          setWorkspaceSection(WorkspaceSection.EXPORT);
        },
      });
    }
  }, [projectExportStatus]);

  return (
    <QueryClientProvider client={queryClient}>
      <ImagePreviewProvider>
        <Content />
      </ImagePreviewProvider>
    </QueryClientProvider>
  );
}

function Content() {
  const isCompactView = useIsCompactView();
  const builds = useProjectBuildsProcessingBuildIds();
  const latestExport = useProjectLatestExport();

  const { processingBuildsChanged } = useProjectBuildActions();
  const { processingExportUpdated, processingExportFinished } =
    useProjectExportsActions();

  const loadAndHandleProcessingBuilds = async () => {
    const projectId = useProjectStore.getState().projectId;
    if (projectId) {
      logProjectEvent(projectId, "builds-polling-fetch-start", {
        currentBuildsCount: builds.length,
        hasLatestExport: !!latestExport,
      });

      try {
        const response = await BuildApi.loadBuilds({
          projectId: projectId!,
          buildTypes: [
            BuildTypeSchema.enum.web,
            BuildTypeSchema.enum.apk,
            BuildTypeSchema.enum.aab,
            BuildTypeSchema.enum.zip,
          ],
          statuses: [
            BuildJobStatusSchema.enum.waiting,
            BuildJobStatusSchema.enum.running,
          ],
        });
        const allProcessingBuilds = response.builds;

        logProjectEvent(projectId, "builds-polling-fetch-success", {
          fetchedBuildsCount: allProcessingBuilds.length,
          buildIds: allProcessingBuilds.map((b) => b.buildId),
          buildTypes: allProcessingBuilds.map((b) => b.buildType),
          buildStatuses: allProcessingBuilds.map((b) => b.status),
        });

        const processingPublishBuilds = allProcessingBuilds.filter(
          (el) => el.buildType !== BuildTypeSchema.enum.zip
        );
        const processingExports = allProcessingBuilds.filter(
          (el) => el.buildType === BuildTypeSchema.enum.zip
        );
        processingBuildsChanged(processingPublishBuilds);
        if (processingExports.length === 0) {
          processingExportFinished();
        } else {
          processingExportUpdated(processingExports[0]);
        }

        return allProcessingBuilds;
      } catch (error) {
        logProjectEvent(projectId, "builds-polling-fetch-error", {
          error: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
        });
        throw error;
      }
    } else {
      logProjectEvent("unknown", "builds-polling-no-project-id", {
        note: "loadAndHandleProcessingBuilds called without projectId",
      });
    }
    return null;
  };

  useQuery({
    queryKey: ["buildsPolling", builds, latestExport],
    queryFn: async () => await loadAndHandleProcessingBuilds(),
    refetchInterval: (data) => {
      if (
        data &&
        Array.isArray(data.state.data) &&
        data.state.data.length > 0
      ) {
        return 5000;
      }

      return false;
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 3,
  });

  // Below useEffect shouldn't be needed, because useQuery with refetchOnWindowFocus should be enough, but it seems like it's not working properly sometimes
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const projectId = useProjectStore.getState().projectId;

      if (projectId) {
        logProjectEvent(projectId, "builds-polling-visibility-change", {
          visibilityState: document.visibilityState,
          willFetch: document.visibilityState === "visible",
        });
      }

      if (document.visibilityState !== "visible") return;
      await loadAndHandleProcessingBuilds();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return isCompactView ? <ProjectPageCompact /> : <ProjectPageDefault />;
}
